import styles from './ActionListEditor.module.scss';

import {
  ActionListDialogAction,
  actionListDialogActionFormValueInit,
  messageBoxFormValueInit,
  openLinkFormValueInit,
} from '#/components/ui/messages/model';

import {
  Button,
  Flex,
  TextInput,
  Textarea,
  Box,
  FileInput,
} from '@mantine/core';

import { IconMinus, IconPlus, IconUpload } from '@tabler/icons-react';

import { wrapImageUploadProps } from '#/components/ui/messages/MessageEditor/MessageImageUpload';

import { useActionEditorFormContext } from '../../context';

import { makeKFunc, SelectSubAction } from '../utils';
import { OpenLinkFields } from '../OpenLink';
import { MessageBoxFields } from '../MessageBox';

import { ActionEditorConfig } from '../types';

const mandarinNumFormatter = new Intl.NumberFormat('zh-Hans-CN-u-nu-hanidec');

export const ActionListDialogFields = ({
  keyPrefix,
}: {
  keyPrefix?: string;
}) => {
  const form = useActionEditorFormContext();
  const k = makeKFunc(keyPrefix);

  const type = form.getInputProps(k`type`).value;

  if (type !== 'action_list_dialog') {
    return null;
  }

  const imageFieldOps = wrapImageUploadProps({
    value: form.getInputProps(k`attribute_schema.image`).value,
    setValue: (v) => form.setFieldValue(k`attribute_schema.image`, v),
  });

  const actions = form.getInputProps(k`attribute_schema.actions`)
    .value as ActionListDialogAction['attribute_schema']['actions'];

  return (
    <>
      <TextInput
        label="標題"
        withAsterisk
        placeholder="請輸入標題，建議 20 字內。"
        key={form.key(k`attribute_schema.title`)}
        {...form.getInputProps(k`attribute_schema.title`)}
      />
      <Textarea
        label="內文"
        withAsterisk
        placeholder="請輸入內文，建議 50 字內。"
        minRows={3}
        autosize
        key={form.key(k`attribute_schema.message`)}
        {...form.getInputProps(k`attribute_schema.message`)}
      />
      <FileInput
        accept="image/png,image/jpeg"
        placeholder="比例：280x172 px（橫式）"
        rightSection={<IconUpload />}
        label="圖片"
        error={form.getInputProps(k`attribute_schema.image`).error}
        value={imageFieldOps.file}
        onChange={(file) => {
          if (!file) return;
          imageFieldOps.upload(file);
        }}
      />
      {actions.map((action, index) => {
        const buttonName = `按鈕${mandarinNumFormatter.format(index + 1)}`;

        const keyPrefix = `attribute_schema.actions.${index}`;

        return (
          <Flex
            className="border-t border-gray-200 pt-3"
            justify="flex-start"
            direction="column"
            gap={10}
          >
            <TextInput
              label={`${buttonName}的文案`}
              withAsterisk
              placeholder="請輸入標題，建議 20 字內。"
              key={form.key(k`${keyPrefix}.button_name`)}
              {...form.getInputProps(k`${keyPrefix}.button_name`)}
            />
            <SelectSubAction
              label={`${buttonName}的點擊行為`}
              withAsterisk
              key={form.key(k`${keyPrefix}.action.type`)}
              {...form.getInputProps(k`${keyPrefix}.action.type`)}
              onChange={(value) => {
                if (value === 'open_link') {
                  form.setFieldValue(
                    k`${keyPrefix}.action`,
                    openLinkFormValueInit,
                  );
                }

                if (value === 'message_box') {
                  form.setFieldValue(
                    k`${keyPrefix}.action`,
                    messageBoxFormValueInit,
                  );
                }
              }}
            />

            <Box pl={10}>
              {/* Sub actinos */}
              {action.action.type === 'open_link' && (
                <OpenLinkFields keyPrefix={k`${keyPrefix}.action`} />
              )}
              {action.action.type === 'message_box' && (
                <MessageBoxFields keyPrefix={k`${keyPrefix}.action`} />
              )}
            </Box>
            {actions.length > 1 && (
              <Button
                variant="transparent"
                color="red"
                onClick={() => {
                  form.removeListItem(k`attribute_schema.actions`, index);
                }}
              >
                <IconMinus />
                刪除按鈕
              </Button>
            )}
          </Flex>
        );
      })}
    </>
  );
};

export const ActionListDialogPreview = ({
  value,
}: {
  value: ActionListDialogAction;
}) => {
  const { attribute_schema: attributes } = value;

  return (
    <div className={styles.preview}>
      <div className={styles.hero}>
        <button className={styles.closeBtn} onClick={undefined}>
          ✕
        </button>
        <img src={attributes.image} alt="hero placeholder" />
      </div>

      <div className={styles.body}>
        <div className={styles.title}>{attributes.title ?? `{標題}`}</div>
        <div className={styles.content}>
          {attributes.message ??
            '{內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文內文}'}
        </div>
      </div>

      {attributes.actions.length > 0 && (
        <div className={styles.ctaList}>
          {attributes.actions.map((btn, i) => (
            <button key={i} className={styles.ctaBtn}>
              {btn.button_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ActionListDialogActions = () => {
  const form = useActionEditorFormContext();
  return (
    <Button
      className={'mr-auto'}
      variant="transparent"
      onClick={() => {
        form.insertListItem(
          'attribute_schema.actions',
          actionListDialogActionFormValueInit,
        );
      }}
    >
      <IconPlus />
      新增按鈕
    </Button>
  );
};

export const ActionListDialog: ActionEditorConfig<
  'action_list_dialog',
  ActionListDialogAction
> = {
  key: 'action_list_dialog',
  Preview: ActionListDialogPreview,
  Fields: ActionListDialogFields,
  Actions: ActionListDialogActions,
  name: 'Action list Dialog',
};
